import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "./button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./dialog";
import { Slider } from "./slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";
import { toast } from "sonner";
import { useFrameConfig, FRAME_COLORS, MAT_COLORS } from "./FramedImage";
import { 
  Camera, 
  X, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw, 
  Move, 
  Smartphone,
  AlertCircle,
  Loader2,
  Image as ImageIcon,
  Home,
  Building2,
  Frame,
  Sofa,
  ChevronLeft,
  ChevronRight,
  Download
} from "lucide-react";

// Room mockup data with positioning hints
const ROOM_MOCKUPS = [
  {
    id: "living-room",
    name: "Living Room",
    icon: Sofa,
    image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1200&q=80",
    defaultPosition: { x: 50, y: 35 },
    defaultScale: 25
  },
  {
    id: "modern-lounge",
    name: "Modern Lounge", 
    icon: Home,
    image: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=1200&q=80",
    defaultPosition: { x: 50, y: 40 },
    defaultScale: 30
  },
  {
    id: "office",
    name: "Home Office",
    icon: Building2,
    image: "https://images.unsplash.com/photo-1666618207644-4de0226a3f85?w=1200&q=80",
    defaultPosition: { x: 25, y: 35 },
    defaultScale: 20
  },
  {
    id: "gallery",
    name: "Gallery",
    icon: Frame,
    image: "https://images.unsplash.com/photo-1577083552792-a0d461cb1dd6?w=1200&q=80",
    defaultPosition: { x: 50, y: 45 },
    defaultScale: 35
  },
  {
    id: "bedroom",
    name: "Bedroom",
    icon: Home,
    image: "https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=1200&q=80",
    defaultPosition: { x: 50, y: 40 },
    defaultScale: 28
  }
];

const ARViewOnWall = ({ product, imageUrl, isOpen, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mockupCanvasRef = useRef(null);
  const artworkRef = useRef(null);
  const streamRef = useRef(null);
  const wasOpen = useRef(isOpen);
  const cameraActiveRef = useRef(false);
  
  const [activeTab, setActiveTab] = useState("mockups");
  const [arSupported, setArSupported] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [artworkLoaded, setArtworkLoaded] = useState(false);
  
  // Get default frame config
  const { config: frameConfig } = useFrameConfig();
  
  // Room mockup state
  const [selectedRoom, setSelectedRoom] = useState(0);
  const [roomImageLoaded, setRoomImageLoaded] = useState(false);
  
  // Artwork position and scale
  const [position, setPosition] = useState({ x: 50, y: 40 });
  const [scale, setScale] = useState(25);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Get frame colors from config
  const getFrameStyle = () => {
    if (!frameConfig) {
      return { frameColor: '#1a1a1a', matColor: '#ffffff', frameWidth: 8, matWidth: 4 };
    }
    return {
      frameColor: FRAME_COLORS[frameConfig.frameColor] || '#1a1a1a',
      matColor: MAT_COLORS[frameConfig.matColor] || '#ffffff',
      frameWidth: Math.max(6, frameConfig.frameWidth * 0.25),
      matWidth: frameConfig.matColor !== 'none' ? Math.max(4, frameConfig.matWidth * 0.2) : 0,
    };
  };
  
  // Initialize position on open - use callback pattern
  const initializePosition = useCallback(() => {
    const room = ROOM_MOCKUPS[selectedRoom];
    setPosition(room.defaultPosition);
    setScale(room.defaultScale);
  }, [selectedRoom]);
  
  // Check AR/Camera support
  useEffect(() => {
    if (!isOpen) return;
    
    const checkSupport = async () => {
      const hasCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
      setArSupported(hasCamera);
      setLoading(false);
    };
    
    checkSupport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  
  // Initialize position when dialog opens
  useEffect(() => {
    if (isOpen) {
      initializePosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Start camera
  const startCamera = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const constraints = {
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
      }
      
      setLoading(false);
    } catch (err) {
      console.error("Camera error:", err);
      setError(
        err.name === "NotAllowedError" 
          ? "Camera access denied. Please allow camera access or use Room Mockups instead."
          : "Could not access camera. Try using Room Mockups instead."
      );
      setLoading(false);
      setActiveTab("mockups");
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  // Cleanup on close - only cleanup stream, not state
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      // Reset camera active via ref to avoid state update in effect
      cameraActiveRef.current = false;
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  // Handle room change
  const changeRoom = (direction) => {
    const newIndex = direction === 'next' 
      ? (selectedRoom + 1) % ROOM_MOCKUPS.length
      : (selectedRoom - 1 + ROOM_MOCKUPS.length) % ROOM_MOCKUPS.length;
    
    setSelectedRoom(newIndex);
    setRoomImageLoaded(false);
    const room = ROOM_MOCKUPS[newIndex];
    setPosition(room.defaultPosition);
    setScale(room.defaultScale);
  };

  // Handle drag
  const handleDragStart = (e) => {
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setIsDragging(true);
    setDragStart({ x: clientX - position.x, y: clientY - position.y });
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const container = (activeTab === "camera" ? canvasRef : mockupCanvasRef).current?.getBoundingClientRect();
    if (!container) return;
    
    const newX = ((clientX - dragStart.x) / container.width) * 100;
    const newY = ((clientY - dragStart.y) / container.height) * 100;
    
    setPosition({
      x: Math.max(5, Math.min(95, newX)),
      y: Math.max(5, Math.min(95, newY))
    });
  }, [isDragging, dragStart, activeTab]);

  const handleDragEnd = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove]);

  // Take screenshot (works for both modes)
  const takeScreenshot = (mode = "mockup") => {
    const canvas = document.createElement('canvas');
    const sourceRef = mode === "camera" ? canvasRef : mockupCanvasRef;
    const container = sourceRef.current?.getBoundingClientRect();
    if (!container) return;
    
    canvas.width = container.width * 2;
    canvas.height = container.height * 2;
    const ctx = canvas.getContext('2d');
    
    // Create temp image for background
    const bgImg = new Image();
    bgImg.crossOrigin = "anonymous";
    
    const drawComposite = () => {
      // Draw background
      if (mode === "camera" && videoRef.current) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      } else {
        ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
      }
      
      // Draw artwork
      if (artworkRef.current && artworkLoaded) {
        const artWidth = (canvas.width * scale) / 100;
        const artHeight = artWidth * (artworkRef.current.naturalHeight / artworkRef.current.naturalWidth);
        const artX = (canvas.width * position.x) / 100 - artWidth / 2;
        const artY = (canvas.height * position.y) / 100 - artHeight / 2;
        
        const frameStyle = getFrameStyle();
        
        // Frame
        ctx.fillStyle = frameStyle.frameColor;
        ctx.fillRect(artX - frameStyle.frameWidth, artY - frameStyle.frameWidth, artWidth + frameStyle.frameWidth * 2, artHeight + frameStyle.frameWidth * 2);
        
        // Mat
        if (frameStyle.matWidth > 0) {
          ctx.fillStyle = frameStyle.matColor;
          ctx.fillRect(artX - frameStyle.matWidth, artY - frameStyle.matWidth, artWidth + frameStyle.matWidth * 2, artHeight + frameStyle.matWidth * 2);
        }
        
        // Shadow
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 20;
        ctx.shadowOffsetX = 8;
        ctx.shadowOffsetY = 8;
        
        ctx.drawImage(artworkRef.current, artX, artY, artWidth, artHeight);
      }
      
      // Download
      const link = document.createElement('a');
      link.download = `${product?.title || 'artwork'}-in-${mode === "camera" ? "your-space" : ROOM_MOCKUPS[selectedRoom].name.toLowerCase().replace(' ', '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success("Image saved!");
    };
    
    if (mode === "camera") {
      drawComposite();
    } else {
      bgImg.onload = drawComposite;
      bgImg.src = ROOM_MOCKUPS[selectedRoom].image;
    }
  };

  const resetPosition = () => {
    const room = ROOM_MOCKUPS[selectedRoom];
    setPosition(room.defaultPosition);
    setScale(room.defaultScale);
  };

  const getDimensionsText = () => {
    if (product?.dimensions) {
      const { width, height } = product.dimensions;
      if (width && height) return `${width}cm × ${height}cm`;
    }
    return null;
  };

  const currentRoom = ROOM_MOCKUPS[selectedRoom];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] h-[90vh] p-0 overflow-hidden bg-black">
        <DialogHeader className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-white text-lg">View on Wall</DialogTitle>
              <DialogDescription className="text-white/70 text-sm">
                {product?.title} {getDimensionsText() && `• ${getDimensionsText()}`}
              </DialogDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
          {/* Tab Switcher */}
          <div className="absolute top-16 left-0 right-0 z-20 flex justify-center">
            <TabsList className="bg-black/60 backdrop-blur-sm">
              <TabsTrigger value="mockups" className="gap-2 data-[state=active]:bg-amber-600">
                <Home className="w-4 h-4" /> Room Mockups
              </TabsTrigger>
              <TabsTrigger value="camera" className="gap-2 data-[state=active]:bg-amber-600">
                <Camera className="w-4 h-4" /> Your Camera
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Room Mockups Tab */}
          <TabsContent value="mockups" className="h-full m-0 mt-0">
            <div className="relative w-full h-full bg-slate-900" ref={mockupCanvasRef}>
              {/* Room Image */}
              <img
                src={currentRoom.image}
                alt={currentRoom.name}
                className="w-full h-full object-cover"
                onLoad={() => setRoomImageLoaded(true)}
                crossOrigin="anonymous"
              />
              
              {/* Loading overlay */}
              {!roomImageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
                </div>
              )}

              {/* Artwork Overlay */}
              {roomImageLoaded && (
                <div
                  className="absolute cursor-move select-none"
                  style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    transform: 'translate(-50%, -50%)',
                    width: `${scale}%`,
                    touchAction: 'none'
                  }}
                  onMouseDown={handleDragStart}
                  onTouchStart={handleDragStart}
                >
                  <div className="relative" style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                    {/* Frame layer (outermost) */}
                    <div 
                      className="absolute rounded-sm z-0" 
                      style={{ 
                        inset: `-${getFrameStyle().frameWidth + getFrameStyle().matWidth}px`,
                        backgroundColor: getFrameStyle().frameColor 
                      }} 
                    />
                    {/* Mat layer (between frame and image) */}
                    {getFrameStyle().matWidth > 0 && (
                      <div 
                        className="absolute rounded-sm z-10" 
                        style={{ 
                          inset: `-${getFrameStyle().matWidth}px`,
                          backgroundColor: getFrameStyle().matColor 
                        }} 
                      />
                    )}
                    {/* Artwork */}
                    <img
                      ref={artworkRef}
                      src={imageUrl}
                      alt={product?.title}
                      className="relative block w-full h-auto z-20"
                      onLoad={() => setArtworkLoaded(true)}
                      draggable={false}
                    />
                    {isDragging && (
                      <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center z-30">
                        <Move className="w-8 h-8 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Room Navigation */}
              <button
                onClick={() => changeRoom('prev')}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => changeRoom('next')}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Room Selector Pills */}
              <div className="absolute top-28 left-0 right-0 flex justify-center gap-2 px-4">
                {ROOM_MOCKUPS.map((room, idx) => {
                  const Icon = room.icon;
                  return (
                    <button
                      key={room.id}
                      onClick={() => {
                        setSelectedRoom(idx);
                        setRoomImageLoaded(false);
                        setPosition(room.defaultPosition);
                        setScale(room.defaultScale);
                      }}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        selectedRoom === idx 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-black/40 text-white/80 hover:bg-black/60'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {room.name}
                    </button>
                  );
                })}
              </div>

              {/* Controls */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 pb-6">
                <div className="max-w-md mx-auto mb-4">
                  <div className="flex items-center gap-4 text-white">
                    <ZoomOut className="w-4 h-4 text-white/60" />
                    <Slider
                      value={[scale]}
                      onValueChange={([v]) => setScale(v)}
                      min={10}
                      max={60}
                      step={1}
                      className="flex-1"
                    />
                    <ZoomIn className="w-4 h-4 text-white/60" />
                  </div>
                  <p className="text-center text-white/50 text-xs mt-1">
                    Drag artwork to reposition • Slider to resize
                  </p>
                </div>

                <div className="flex items-center justify-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetPosition}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" /> Reset
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => takeScreenshot("mockup")}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Download className="w-4 h-4 mr-2" /> Save Image
                  </Button>
                </div>
              </div>

              {/* Instructions */}
              {!isDragging && (
                <div className="absolute top-40 left-0 right-0 flex justify-center pointer-events-none">
                  <div className="bg-black/60 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-full flex items-center gap-2">
                    <Move className="w-4 h-4" />
                    <span>Drag the artwork to position it</span>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Camera Tab */}
          <TabsContent value="camera" className="h-full m-0 mt-0">
            <div className="relative w-full h-full bg-black" ref={canvasRef}>
              {/* Camera States */}
              {activeTab === "camera" && !cameraActive && !loading && !error && (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-900 to-black z-10 p-8">
                  <div className="text-center text-white max-w-md">
                    <div className="w-24 h-24 rounded-full bg-amber-600/20 flex items-center justify-center mx-auto mb-6">
                      <Camera className="w-12 h-12 text-amber-400" />
                    </div>
                    <h3 className="text-2xl font-semibold mb-3">Use Your Camera</h3>
                    <p className="text-white/70 mb-6">
                      Point your camera at a wall to see how the artwork would look in your actual space.
                    </p>
                    <Button onClick={startCamera} size="lg" className="w-full bg-amber-600 hover:bg-amber-700">
                      <Camera className="w-5 h-5 mr-2" /> Start Camera
                    </Button>
                    <p className="text-white/50 text-xs mt-4">
                      Or use Room Mockups if camera is unavailable
                    </p>
                  </div>
                </div>
              )}

              {activeTab === "camera" && loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
                  <div className="text-center text-white">
                    <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
                    <p>Starting camera...</p>
                  </div>
                </div>
              )}

              {activeTab === "camera" && error && (
                <div className="absolute inset-0 flex items-center justify-center bg-black z-10 p-8">
                  <div className="text-center text-white max-w-md">
                    <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Camera Unavailable</h3>
                    <p className="text-white/70 mb-6">{error}</p>
                    <div className="space-y-3">
                      <Button onClick={startCamera} className="w-full bg-amber-600 hover:bg-amber-700">
                        Try Again
                      </Button>
                      <Button variant="outline" onClick={() => setActiveTab("mockups")} className="w-full border-white/30 text-white hover:bg-white/10">
                        Use Room Mockups Instead
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Video Feed */}
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
                style={{ display: cameraActive ? 'block' : 'none' }}
              />

              {/* Artwork Overlay for Camera */}
              {cameraActive && (
                <>
                  <div
                    className="absolute cursor-move"
                    style={{
                      left: `${position.x}%`,
                      top: `${position.y}%`,
                      transform: 'translate(-50%, -50%)',
                      width: `${scale}%`,
                      touchAction: 'none'
                    }}
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                  >
                    <div className="relative" style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}>
                      <div className="absolute -inset-2 bg-gradient-to-br from-amber-100 to-amber-200 rounded-sm" />
                      <div className="absolute -inset-1 bg-white rounded-sm" />
                      <img
                        src={imageUrl}
                        alt={product?.title}
                        className="relative block w-full h-auto"
                        draggable={false}
                      />
                      {isDragging && (
                        <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                          <Move className="w-8 h-8 text-white drop-shadow-lg" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Camera Controls */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 pb-6 z-10">
                    <div className="max-w-md mx-auto mb-4">
                      <div className="flex items-center gap-4 text-white">
                        <ZoomOut className="w-4 h-4 text-white/60" />
                        <Slider
                          value={[scale]}
                          onValueChange={([v]) => setScale(v)}
                          min={10}
                          max={80}
                          step={1}
                          className="flex-1"
                        />
                        <ZoomIn className="w-4 h-4 text-white/60" />
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resetPosition}
                        className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" /> Reset
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => takeScreenshot("camera")}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" /> Save Photo
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={stopCamera}
                        className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                      >
                        <X className="w-4 h-4 mr-2" /> Stop
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ARViewOnWall;
